import { useState, useEffect, createContext, useContext } from "react";
import api, { setToken } from "../api";

export interface User {
  id: number;
  first_name: string;
  username: string;
  telegram_id: number;
  wallet_address: string | null;
  created_at: string;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

const DEV_USER: User = {
  id: 0,
  first_name: "Dev",
  username: "dev_user",
  telegram_id: 0,
  wallet_address: null,
  created_at: new Date().toISOString(),
};

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    async function authenticate() {
      try {
        // Wait for Telegram WebApp SDK to initialize (may take a moment
        // from inline buttons in group chats — the WebView spins up
        // slower than a direct bot launch).
        let tg = window.Telegram?.WebApp;
        if (!tg?.initData) {
          await new Promise((r) => setTimeout(r, 500));
          tg = window.Telegram?.WebApp;
        }
        if (!tg?.initData) {
          await new Promise((r) => setTimeout(r, 1500));
          tg = window.Telegram?.WebApp;
        }
        if (!tg?.initData) {
          await new Promise((r) => setTimeout(r, 2000));
          tg = window.Telegram?.WebApp;
        }

        // CRITICAL: always call ready() so Telegram dismisses its native
        // loading overlay. Without this, even if the app renders an error
        // screen, Telegram's spinner covers it and the user sees nothing.
        if (tg) {
          tg.ready();
          tg.expand();
        }

        const hasInitData = tg?.initData;

        if (!hasInitData) {
          if (import.meta.env.DEV) {
            setState({ user: DEV_USER, loading: false, error: null });
            return;
          }
          setState({ user: null, loading: false, error: "Open in Telegram" });
          return;
        }

        // Try cached token first (survives WebView reload after Tonkeeper)
        const cached = sessionStorage.getItem("sb_token");
        if (cached) {
          try {
            const me = await api.get("/users/me", { timeout: 10000 });
            setState({ user: me.data, loading: false, error: null });
            return;
          } catch {
            sessionStorage.removeItem("sb_token");
            delete api.defaults.headers.common["Authorization"];
          }
        }

        const res = await api.post("/auth/telegram", { init_data: tg!.initData }, { timeout: 10000 });
        const { access_token, user } = res.data;

        setToken(access_token);
        setState({ user, loading: false, error: null });
      } catch (err) {
        // Ensure Telegram's native spinner is dismissed even on crash
        try { window.Telegram?.WebApp?.ready(); } catch {}
        setState({
          user: null,
          loading: false,
          error: err instanceof Error ? err.message : "Auth failed",
        });
      }
    }

    authenticate();
  }, []);

  return { ...state, isDev: import.meta.env.DEV && state.user === DEV_USER };
}

// Context so child components can access user without re-fetching
const AuthContext = createContext<{ user: User | null }>({ user: null });
export const AuthProvider = AuthContext.Provider;
export function useUser() {
  return useContext(AuthContext).user;
}
